import Cookies from 'universal-cookie';

export type AvailableLocale = 'en' | 'fr' | 'es' | 'de' | 'it' | 'nl';

export const LOCALES: AvailableLocale[] = ['en', 'fr', 'es', 'de', 'it', 'nl'];
export const DEFAULT_LOCALE: AvailableLocale = 'en';

export const LOCALE_LABELS: Record<AvailableLocale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  de: 'Deutsch',
  it: 'Italiano',
  nl: 'Nederlands',
};

const cookies = new Cookies();

export const LOCALE_QUERY_PARAM = 'locale';

function normalizeLocale(value: string): AvailableLocale | null {
  const normalized = value.trim().toLowerCase();
  return LOCALES.includes(normalized as AvailableLocale) ? (normalized as AvailableLocale) : null;
}

export function parseLocaleFromSearch(search: string): AvailableLocale | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const locale = params.get(LOCALE_QUERY_PARAM);

  if (!locale) return null;

  return normalizeLocale(locale);
}

export function appendLocaleToUrl(url: string, locale: AvailableLocale): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set(LOCALE_QUERY_PARAM, locale);
    return parsed.toString();
  } catch {
    return url;
  }
}

export function getLocale(): AvailableLocale {
  const urlLocale =
    typeof window !== 'undefined' ? parseLocaleFromSearch(window.location.search) : null;

  if (urlLocale) {
    cookies.set('locale', urlLocale, { path: '/' });
    return urlLocale;
  }

  const savedLocale = cookies.get('locale');

  if (LOCALES.includes(savedLocale)) return savedLocale;

  return DEFAULT_LOCALE;
}

export function setLocale(locale: AvailableLocale): void {
  if (!LOCALES.includes(locale)) return;
  cookies.set('locale', locale, { path: '/' });
}

async function loadEnglishMessages(): Promise<Record<string, string>> {
  try {
    const module = await import('../../lang/en.json');
    return module.default;
  } catch {
    return {};
  }
}

export async function loadLocaleMessages(locale: AvailableLocale): Promise<Record<string, string>> {
  const targetLocale = locale ?? DEFAULT_LOCALE;

  if (targetLocale === DEFAULT_LOCALE) {
    return loadEnglishMessages();
  }

  try {
    const module = await import(`../../lang/${targetLocale}.json`);
    const messages = module.default;

    if (Object.keys(messages).length === 0) {
      return loadEnglishMessages();
    }

    return messages;
  } catch {
    return loadEnglishMessages();
  }
}
